import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const pad = (n) => String(n).padStart(2, "0");
const isoOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// TheFork-style month picker: dot under days with bookings, closed days greyed, quick jump.
// `fetchMonth(yyyy-mm)` must resolve {days:{date:{count}}, closed_weekdays, closed_dates}.
export function DayPicker({ value, onChange, fetchMonth, trigger, dark = false, onClosure, refreshKey = 0 }) {
  const [open, setOpen] = useState(false);
  const [ym, setYm] = useState(value.slice(0, 7));
  const [data, setData] = useState({});
  const [menu, setMenu] = useState(null); // {date}
  const pressTimer = useRef(null);
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });

  useEffect(() => { if (open) setYm(value.slice(0, 7)); }, [open, value]);
  useEffect(() => { setData({}); }, [refreshKey]);
  useEffect(() => {
    if (!open || data[ym]) return;
    fetchMonth(ym).then((d) => setData((cur) => ({ ...cur, [ym]: d }))).catch(() => {});
  }, [open, ym, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [y, m] = ym.split("-").map(Number);
  const cells = useMemo(() => {
    const first = new Date(y, m - 1, 1);
    const lead = (first.getDay() + 6) % 7;
    const n = new Date(y, m, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => i + 1)];
  }, [y, m]);
  const info = data[ym] || {};
  const closedWd = info.closed_weekdays || [];
  const closedDates = info.closed_dates || [];
  const closures = info.closures || {};
  const svcKeys = Object.keys(info.services || {});
  const shiftMonth = (k) => { const d = new Date(y, m - 1 + k, 1); setYm(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`); };
  const pick = (d) => { onChange(d); setOpen(false); };
  const applyClosure = async (date, services) => {
    setMenu(null);
    await onClosure?.(date, services);
    setData((cur) => { const c = { ...cur }; delete c[ym]; return c; });
  };
  const startPress = (date) => { if (!onClosure) return; pressTimer.current = setTimeout(() => setMenu({ date }), 550); };
  const endPress = () => clearTimeout(pressTimer.current);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-3 rounded-3xl border-[#e8dcc8] shadow-xl" data-testid="daypicker">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => shiftMonth(-1)} className="h-9 w-9 rounded-full hover:bg-[#faf3ee] flex items-center justify-center" data-testid="daypicker-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-bold text-[#2b1410]" data-testid="daypicker-month">{MONTHS[m - 1]} {y}</span>
          <button onClick={() => shiftMonth(1)} className="h-9 w-9 rounded-full hover:bg-[#faf3ee] flex items-center justify-center" data-testid="daypicker-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-[#7c5a4e] mb-1">{WD.map((w) => <span key={w}>{w}</span>)}</div>
        <div className="grid grid-cols-7 gap-y-1">
          {cells.map((d, i) => {
            if (!d) return <span key={`e${i}`} />;
            const date = isoOf(y, m - 1, d);
            const wd = (new Date(y, m - 1, d).getDay() + 6) % 7;
            const cl = closures[date] || [];
            const fullyClosed = closedWd.includes(wd) || closedDates.includes(date) || (svcKeys.length > 0 && svcKeys.every((k) => cl.includes(k)));
            const partClosed = !fullyClosed && cl.length > 0;
            const closed = fullyClosed;
            const count = info.days?.[date]?.count || 0;
            const sel = date === value;
            return (
              <button key={date} onClick={() => { if (menu) { setMenu(null); return; } pick(date); }} onContextMenu={(e) => { if (onClosure) { e.preventDefault(); setMenu({ date }); } }}
                onPointerDown={() => startPress(date)} onPointerUp={endPress} onPointerLeave={endPress} data-testid={`daypicker-day-${date}`} data-count={count} data-closed={fullyClosed ? "all" : partClosed ? cl.join(",") : ""}
                className={`relative mx-auto h-10 w-10 rounded-full text-sm font-semibold flex flex-col items-center justify-center transition-colors select-none ${sel ? "bg-[#4a1c17] text-white" : closed ? "text-[#c9b8a8] line-through" : date === today ? "text-[#C48B31] hover:bg-[#faf3ee]" : "text-[#2b1410] hover:bg-[#faf3ee]"}`}>
                {d}
                {partClosed && <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-rose-500 ring-1 ring-white" title={`Chiuso: ${cl.map((k) => info.services?.[k] || k).join(", ")}`} />}
                {count > 0 && <span className={`absolute bottom-1 h-1.5 w-1.5 rounded-full ${sel ? "bg-[#C48B31]" : "bg-[#4a1c17]"}`} />}
              </button>
            );
          })}
        </div>
        {menu && (
          <div className="mt-2 rounded-2xl border border-rose-200 bg-rose-50 p-2 text-xs" data-testid="daypicker-closure-menu">
            <div className="font-bold text-rose-900 mb-1.5">{menu.date.split("-").reverse().join("/")} · chiusura prenotazioni online</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => applyClosure(menu.date, svcKeys)} className="rounded-full bg-rose-600 text-white px-3 py-1 font-bold" data-testid="closure-all">Chiudi giornata</button>
              {svcKeys.map((k) => <button key={k} onClick={() => applyClosure(menu.date, [k])} className="rounded-full border border-rose-300 bg-white text-rose-800 px-3 py-1 font-bold" data-testid={`closure-${k}`}>Chiudi solo {info.services[k]}</button>)}
              <button onClick={() => applyClosure(menu.date, [])} className="rounded-full bg-emerald-600 text-white px-3 py-1 font-bold" data-testid="closure-reopen">Riapri</button>
              <button onClick={() => setMenu(null)} className="rounded-full px-2 py-1 text-[#7c5a4e]">annulla</button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#f0e4d6] text-xs text-[#7c5a4e]">
          <span className="inline-flex items-center gap-1 flex-wrap"><i className="h-1.5 w-1.5 rounded-full bg-[#4a1c17] inline-block" /> prenotazioni · <span className="text-[#c9b8a8] line-through">1</span> chiuso · <i className="h-2 w-2 rounded-full bg-rose-500 inline-block" /> un servizio</span>
          <button onClick={() => pick(today)} className="rounded-full bg-[#faf3ee] px-3 py-1 font-bold text-[#4a1c17]" data-testid="daypicker-today">Oggi</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const DayPickerIcon = () => <CalendarDays className="h-3.5 w-3.5 opacity-70" />;
